/**
 * Config-related strings.
 */
import React from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import Icons from '../ui/IconButton/Icons';

export default {
    // Labels
    d_myAccount: 'My Account',
    h_myAccount: 'Change your password and manage your subscription payment method.',

    d_firmInfo: 'Firm Info',
    h_firmInfo: 'Manage the legal, billing and display name/logo for your firm.',

    d_users: 'Users',
    h_users: 'Manage your users, including name, email, target billable hours, billable rate and roles.',

    d_groups: 'Groups',
    h_groups: <div>
        <p>Create groups that can be used when assigning tasks. When a task is assigned to a group, it can be claimed
            and worked by any member of that group.</p>
        <p>This is useful when creating tasks that can be done by more than one person, without having to reassign the
            task when the assigned user becomes overloaded.</p>
    </div>,

    d_bankAccounts: 'Bank Accounts',
    h_bankAccounts: 'View your operating and IOLTA accounts, and view the current balance of the IOLTA account.',

    d_general: 'General',
    h_general: <div>
        <p>Miscellaneous options affecting tasks, leads and sounds.</p>
    </div>,

    d_invoicing: 'Invoicing',
    h_invoicing: <div>
        <p>Set up options for invoicing, including billing increments, time rounding methods, letterhead and fonts.</p>
    </div>,

    d_categories: 'Categories',
    h_categories: <div>
        <p>Edit and add task categories (e.g. Administrative).</p>
        <p>The standard list is usually exhaustive, but sometimes additional categories make sense, especially with
            Administrative tasks.</p>
        <p>For example, if you spend a lot of time on the phone with vendors or technical support, you could create
            new categories for those.</p>
        <p>The benefit of creating a new category (versus just creating an activity type) is that you can filter
            tasks by category in the Task Grid.</p>
    </div>,

    d_caseTypes: 'Case Types',
    h_caseTypes: <div>
        <p>Create and modify the list of case types that you accept.</p>
    </div>,

    d_activities: 'Activities',
    h_activities: <div>
        <p>Create and modify activity types related to your categories.</p>
    </div>,

    d_dockets: 'Dockets',
    h_dockets: <div>
        <p>If you regularly go to multiple docket calls, you can add the dockets and their time slots, then assign
            tasks to the relevant docket.</p>
    </div>,

    d_lookups: 'Lookups',
    h_lookups: <div>
        <p>Lookups provide a method to customize the software by editing lists of possible entries throughout the
            software.</p>
        <p>Examples include:</p>
        <ul>
            <li>Won/lost/refused reasons</li>
            <li>Relationships and participant types</li>
            <li>Discount reasons</li>
            <li>Event types</li>
        </ul>
    </div>,

    d_export: 'Export',
    h_export: <div>
        <p>Export the entire system for use in another program.</p>
    </div>,

    // Cards inside Config (where required)

    // General Card
    d_ensureActivity: 'Ensure an activity type is selected',
    h_ensureActivity: '<div><p>When editing a task, ensure that an activity type is selected for every task (if the category has related activity types).</p></div>',

    d_ensureCategory: 'Ensure a category is selected',
    h_ensureCategory: 'When editing a task, ensure that at least one category is selected.',

    d_ensureCategoryRelationship: 'Ensure category relationships are enforced',
    h_ensureCategoryRelationship: '<div><p>When editing a task with a selected category related to marketing, sales or clients, ensure that a related Case, Lead or Campaign is selected.</p></div>',

    d_emojiHelp: 'Turn on emoji support for tasks',
    h_emojiHelp: 'Turn on emoji support for tasks. This lets you quickly express your feelings about a task by adding one or more emojis. Use with caution around snowflakes and millennials.',

    d_activity: 'Activity',
    h_activity: (params: { paidConsultName: string }) => `<div>Which activity type should be associated with the ${params.paidConsultName}?</div>`,

    d_activityFollowup: 'Activity Follow-Up',
    h_activityFollowup: 'Which activity type should be associated with follow-up tasks for leads?',

    d_paidConsultsName: 'Name',
    h_paidConsultsName: <div>
        <p>This is the name that will be used throughout the user interface when referring to a paid consult.</p>
    </div>,

    d_paidConsults: 'Our firm charges for initial consultations',
    h_paidConsults: '<div><p>If your firm charges for initial consultations (or case strategy meetings), check here. This will enable additional logic in lead management to take payments before opening a case, and add an extra milestone to mark a lead as completed/won, without creating a case.</p><p>You can override this setting by case type, or by individual lead. This just sets the default.</p></div>',

    d_initialConsultUser: 'Initial Consult User',
    h_initialConsultUser: (params: { paidConsultName: string }) => `<div><p>Which user should be used to do ${params.paidConsultName}?</p></div>`,

    d_showHelp: 'Show Help Icons',
    h_showHelp: <div>
        Show <FontAwesomeIcon icon={Icons.HELP.reactIcon} color='green'/> Help icons. These help icons are useful when learning to navigate and use Outlaw. Experiences users can turn the help system off.
    </div>,

    d_advancedDiagnostics: 'Turn on Advanced Diagnostics',
    h_advancedDiagnostics: 'Turn on advanced diagnostics that will help your Support person diagnose issues.',

    d_wssLogging: 'Enable WebSocket Logging',
    h_wssLogging: 'Used to help diagnose connectivity issues between your application and the server.',

    d_authLogging: 'Enable Authentication Logging',
    h_authLogging: 'Logs calls between the application and the authentication server. Passwords are NEVER logged.',

    d_apiLogging: 'Enable API Logging',
    h_apiLogging: 'Logs all calls between the application and the server.',

    d_queueLogging: 'Enable Queue Logging',
    h_queueLogging: 'Logs queueing of data, as well as the processing of the queues. Queueing is the mechanism that Outlaw Practice uses to ensure changes made while offline are applied as soon as the application is back online.',

    d_reduxLogging: 'Enable Data Logging',
    h_reduxLogging: 'Logs the creation, editing and deletion of data that you enter into the application.',

    d_restoreLogging: 'Enable Restore Logging',
    h_restoreLogging: 'Logs the restoration process, which loads offline data to the application when you do not have an internet connection.',

    d_ringBell: 'Ring bell when task is completed?',
    h_ringBell: 'When completing a task, should a bell sound off to congratulate you?',

    d_victorySounds: 'Play victory sounds when a lead is won?',
    h_victorySounds: 'When converting a lead to a case, should a victory sound be played?',

    // Firm Info
    d_legalName: 'Legal Name',
    h_legalName: 'The legal entity name of your business',

    d_billingName: 'Billing Name',
    h_billingName: 'The name for your business on invoices and official correspondence',

    d_displayName: 'Display Name',
    h_displayName: 'The name for your business at the top of this application',

    d_firmLogo: 'Firm Logo',
    h_firmLogo: '<div><p>The logo associated with your firm. This will be displayed on the client portal.</p></div>',

    d_firmTimeZone: 'Firm Time Zone',
    h_firmTimeZone: '<div><p>The time zone your firm keeps its books in. Invoices and trust balances count each payment, deposit, task and expense on its date in this time zone, so everyone at the firm sees the same figures wherever they work.</p><p>If no time zone is set, each person\'s computer time zone is used. Once set, the time zone can be changed but not removed.</p></div>',

    // Red flag importer
    d_import: 'Import Standard Flags',
    h_import: <div>
        <p>Outlaw Practice Provides dozens of red flags for leads and cases.</p>
    </div>,

    d_processorEmails: 'Upon Successfully Charging a Payment',
    h_processorEmails: '<div><p>When any transaction is made, whether successful or otherwise, emails can be sent to any of the following parties:</p><ul><li>The person making the payment</li><li>The client(s)</li><li>Your firm</li></ul><p><i>Note that if the payer and the client are the same person, only one email will be sent to that person.</i></p><p>To send emails to your firm, fill in the To field. You can add additional recipients in the CC or BCC fields.</p></div>',


};
